import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { GradientMesh } from '@/components/ui/GradientMesh'
import { motion, AnimatePresence } from 'framer-motion'
import { Eye, EyeOff } from 'lucide-react'

export default function SignupPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { signUpWithEmail } = useAppStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (password !== confirmPassword) { setError('Passwords do not match.'); return }
    setLoading(true)
    const result = await signUpWithEmail(email, password, name)
    setLoading(false)
    if (result.error) { setError(result.error.message); return }
    navigate(inviteToken ? `/invite/${inviteToken}` : '/')
  }

  return (
    <div className="min-h-dvh relative overflow-hidden" style={{ background: '#0d0d0d' }}>
      {/* Full-page gradient — visible on mobile, masked by solid left col on desktop */}
      <div className="absolute inset-0 pointer-events-none">
        <GradientMesh colors={['#e63946', '#a78bfa', '#111133']} distortion={8} swirl={0.18} speed={0.9} rotation={110} waveAmp={0.22} waveFreq={18} waveSpeed={0.18} grain={0.05} />
      </div>

      <div className="relative min-h-dvh grid lg:grid-cols-2">
      {/* Left — form */}
      <div className="flex flex-col p-8 md:p-12 lg:bg-[#0d0d0d]">
        <div className="flex items-center gap-2.5 mb-auto">
          <div className="w-7 h-7 rounded-lg bg-accent/20 flex items-center justify-center text-accent text-xs font-bold flex-shrink-0">P</div>
          <span className="font-semibold text-sm text-text tracking-tight">PRJCT</span>
        </div>

        <div className="flex flex-1 items-center justify-center py-12">
          <div className="w-full max-w-[340px]">
            <div className="rounded-2xl p-7 lg:p-0 lg:rounded-none bg-black/75 lg:bg-transparent backdrop-blur-2xl lg:backdrop-blur-none border border-white/[0.08] lg:border-0">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            >
              <div className="mb-7">
                <h1 className="text-2xl font-bold text-text tracking-tight">Create account</h1>
                <p className="text-sm text-text-muted mt-1">
                  {inviteToken ? "Set up your account to accept the invite" : "Start your workspace in seconds"}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Field label="Full name">
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Porter Gerlach" className="h-10" required autoFocus />
                </Field>

                <Field label="Email">
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.co" className="h-10" required />
                </Field>

                <Field label="Password">
                  <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                </Field>

                <Field label="Confirm password">
                  <PasswordInput value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" />
                </Field>

                <AnimatePresence>
                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2"
                    >
                      {error}
                    </motion.p>
                  )}
                </AnimatePresence>

                <Button type="submit" variant="primary" size="lg" className="w-full mt-1" disabled={loading}>
                  {loading ? <span className="flex items-center gap-2"><Spinner /> Creating…</span> : 'Create account'}
                </Button>
              </form>

              <p className="text-center text-sm text-text-muted mt-6">
                Already have an account?{' '}
                <Link
                  to={inviteToken ? `/auth?invite=${inviteToken}` : '/auth'}
                  className="text-accent hover:underline underline-offset-4 transition-colors"
                >
                  Sign in
                </Link>
              </p>
            </motion.div>
            </div>
          </div>
        </div>

        <p className="text-xs text-text-faint mt-auto">© {new Date().getFullYear()} BLANQ. All rights reserved.</p>
      </div>

      {/* Right — quote only; gradient bleeds from full-page layer */}
      <div className="relative hidden lg:block overflow-hidden">
        <div className="absolute inset-0 flex flex-col justify-end p-12 pointer-events-none">
          <div style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(1px)', borderRadius: 16, padding: '24px 28px', border: '1px solid rgba(255,255,255,0.07)', maxWidth: 400 }}>
            <p className="text-white/90 text-base font-medium leading-snug" style={{ fontFamily: 'Helvetica Neue, sans-serif', letterSpacing: '-0.01em' }}>
              "Finally a project management tool that doesn't get in the way."
            </p>
            <p className="text-white/40 text-sm mt-3" style={{ fontFamily: 'Helvetica Neue, sans-serif' }}>— Early access team member</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}

function PasswordInput({ value, onChange, placeholder }) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="h-10 pr-10"
        required
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-faint hover:text-text-muted transition-opacity duration-150 active:scale-[0.97]"
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-text-muted">{label}</label>
      {children}
    </div>
  )
}

function Spinner() {
  return (
    <svg className="animate-spin" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  )
}
